<script setup lang="ts">
import { computed, ref } from "vue";
import {
  useContextStore,
  useGroupingStore,
  usePeopleStore,
  useToastStore,
} from "../data/store.ts";
import type { Grouping } from "../data/types";
import { DELETE, POST } from "../data/api.ts";
import DebounceButton from "./DebounceButton.vue";

const contextStore = useContextStore();
const peopleStore = usePeopleStore();
const groups = useGroupingStore();
const toaster = useToastStore();

const personSearch = ref<string>("");
const group = ref<Grouping>(
  groups.group ??
    ({
      id: groups.activeGroup,
      name: "",
      owner: contextStore.user,
      public: false,
      people: [],
    } as Grouping),
);
const isNew = ref<boolean>(!groups.group);

const filteredPeople = computed(() => {
  return peopleStore.sortedPeople.filter(
    (person) =>
      (personSearch.value.length === 0 ||
        person.name.toLowerCase().indexOf(personSearch.value.toLowerCase()) >=
          0) &&
      group.value.people.indexOf(person.id) < 0,
  );
});

const groupLink = computed(
  () =>
    `${window.location.origin}${import.meta.env.TBS_FRONTEND_BASE ?? ""}/#/group/g/${group.value.id}`,
);

const invalidKey = computed(
  () =>
    group.value.id === "NEW" ||
    (group.value.id in groups.groups && isNew.value) ||
    /[^A-Za-z0-9-]/.test(group.value.id),
);

function pickPerson(uid: string) {
  personSearch.value = "";
  group.value.people.push(uid);
}

function dropPerson(uid: string) {
  const idx = group.value.people.indexOf(uid);
  if (idx >= 0) {
    group.value.people.splice(idx, 1);
  }
}

function save() {
  return new Promise<void>((res, rej) => {
    if (invalidKey.value) {
      rej();
    } else {
      POST("/grouping/", {
        ...group.value,
        people: JSON.stringify(group.value.people),
      })
        .then(() => {
          toaster.toast("Saved!", "SUCC");
          isNew.value = false;
          groups.reload();
          res();
        })
        .catch(() => {
          toaster.toast("Failed to save!", "FAIL");
          rej();
        });
    }
  });
}

function del() {
  DELETE("/grouping/", { gid: group.value.id })
    .then(() => {
      toaster.toast("Deleted!", "SUCC");
      groups.reload();
    })
    .catch();
}
</script>

<template>
  <div class="row justify-content-center">
    <div class="card col-lg-8 col text-start">
      <div class="card-body">
        <div class="row">
          <div class="col-10">
            <a :href="groupLink" target="_blank">{{ groupLink }}</a>
          </div>
          <div class="col-2 text-end text-nowrap">
            <button
              class="btn btn-outline-danger btn-sm me-2"
              @click="del"
              v-if="!isNew"
            >
              <i class="bi-trash" />&nbsp;Delete
            </button>
            <DebounceButton
              :exec="save"
              class="btn-sm btn-outline-success"
              icon="bi-floppy"
            >
              Save
            </DebounceButton>
          </div>

          <div class="col-12">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="groupPublic"
                v-model="group.public"
              />
              <label class="form-check-label" for="groupPublic"
                >Make group visible to all</label
              >
            </div>
          </div>
          <div class="col">
            <div class="form-floating mb-3 has-validation">
              <input
                v-model="group.id"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': invalidKey }"
                id="groupKey"
                placeholder="ECS"
              />
              <label for="groupKey">Group key</label>
              <div class="invalid-feedback">
                Please choose a valid and unique group key.
              </div>
            </div>
          </div>
          <div class="col">
            <div class="form-floating mb-3">
              <input
                v-model="group.name"
                type="text"
                class="form-control"
                id="groupName"
                placeholder="Evidence for Climate Solutions"
              />
              <label for="groupName">Group name</label>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <div class="input-group mb-2">
              <i
                class="bi-search border border-end-0 p-2 rounded-start text-muted"
              ></i>
              <input
                v-model="personSearch"
                class="form-control border-start-0 border"
                type="search"
                placeholder="search"
              />
            </div>
            <div class="list-group overflow-auto" style="max-height: 15rem">
              <button
                v-for="person in filteredPeople"
                :key="person.id"
                class="list-group-item list-group-item-action"
                type="button"
                @click="pickPerson(person.id)"
              >
                <i class="bi-bookmark-plus" />
                {{ person.short }}
                <span class="text-muted" style="font-size: 0.6em">
                  ({{ person.name }})
                </span>
              </button>
            </div>
          </div>
          <div class="col-6">
            <h4 class="mb-2 mt-3">Group members</h4>
            <div class="list-group overflow-auto" style="max-height: 15rem">
              <button
                v-for="uid in group.people"
                :key="uid"
                class="list-group-item list-group-item-action"
                type="button"
                @click="dropPerson(uid)"
              >
                <i class="bi-bookmark-x" />
                {{ peopleStore.people?.[uid]?.short }}
                <span class="text-muted" style="font-size: 0.6em">
                  ({{ peopleStore.people?.[uid]?.name }})
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
